/** Общие хелперы открытия файлов в мини-приложении (облако заказа / портфолио). */

export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function fileEmoji(name: string, mime?: string | null): string {
  const ext = (name.split('.').pop() || '').toLowerCase();
  const m = String(mime || '').toLowerCase();
  if (m.startsWith('video/') || ['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext)) return '🎬';
  if (m.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic'].includes(ext)) return '🖼️';
  if (ext === 'pdf' || m.includes('pdf')) return '📄';
  if (['xls', 'xlsx', 'csv'].includes(ext) || m.includes('spreadsheet') || m.includes('excel')) return '📊';
  if (['zip', 'rar', '7z'].includes(ext)) return '🗜️';
  if (['doc', 'docx', 'txt', 'rtf', 'odt'].includes(ext)) return '📝';
  return '📎';
}

export function isImageFile(name: string, mime?: string | null): boolean {
  const m = String(mime || '').toLowerCase();
  if (m.startsWith('image/')) return true;
  const ext = (name.split('.').pop() || '').toLowerCase();
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'bmp'].includes(ext);
}

export function isPdfFile(name: string, mime?: string | null): boolean {
  const m = String(mime || '').toLowerCase();
  if (m.includes('pdf')) return true;
  return (name.split('.').pop() || '').toLowerCase() === 'pdf';
}

/** Word / Excel / PowerPoint — превью через Office Online Viewer */
export function isOfficeFile(name: string, mime?: string | null): boolean {
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'odp', 'rtf'].includes(ext)) {
    return true;
  }
  const m = String(mime || '').toLowerCase();
  return (
    m.includes('officedocument') ||
    m.includes('msword') ||
    m.includes('ms-excel') ||
    m.includes('ms-powerpoint') ||
    m.includes('opendocument')
  );
}

export function canPreviewInApp(name: string, mime?: string | null): boolean {
  return isImageFile(name, mime) || isPdfFile(name, mime) || isOfficeFile(name, mime);
}

export type PreviewKind = 'image' | 'pdf' | 'office';

export function previewKindOf(name: string, mime?: string | null): PreviewKind | null {
  if (isImageFile(name, mime)) return 'image';
  if (isPdfFile(name, mime)) return 'pdf';
  if (isOfficeFile(name, mime)) return 'office';
  return null;
}

export function withInlineParam(url: string): string {
  try {
    const u = new URL(url, window.location.origin);
    u.searchParams.set('inline', '1');
    return u.toString();
  } catch {
    return url.includes('?') ? `${url}&inline=1` : `${url}?inline=1`;
  }
}

export function officeEmbedUrl(fileUrl: string): string {
  return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;
}

export function openExternalUrl(url: string) {
  const tg = (window as any).Telegram?.WebApp;
  if (tg?.openLink) {
    try {
      tg.openLink(url);
      return;
    } catch {
      /* fallthrough */
    }
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

export type FilePreviewState = {
  /** URL для показа (blob / inline / office embed) */
  url: string;
  /** Оригинальная ссылка скачивания с dl_token */
  downloadUrl: string;
  name: string;
  mime?: string | null;
  kind: PreviewKind;
};

const BLOB_PREVIEW_MAX_BYTES = 45 * 1024 * 1024;

/**
 * Готовит превью: картинки/PDF в приложении, Office — Microsoft viewer.
 * Возвращает 'external', если формат не поддерживается.
 */
export async function buildFilePreview(
  downloadUrl: string,
  name: string,
  mime?: string | null,
  sizeBytes?: number | null
): Promise<FilePreviewState | 'external'> {
  const kind = previewKindOf(name, mime);
  if (!kind) return 'external';

  const inlineUrl = withInlineParam(downloadUrl);

  if (kind === 'office') {
    return {
      url: officeEmbedUrl(inlineUrl),
      downloadUrl: inlineUrl,
      name,
      mime,
      kind
    };
  }

  if (kind === 'image') {
    return { url: inlineUrl, downloadUrl: inlineUrl, name, mime, kind };
  }

  const tooBig =
    sizeBytes != null && Number(sizeBytes) > 0 && Number(sizeBytes) > BLOB_PREVIEW_MAX_BYTES;
  if (tooBig) {
    return { url: inlineUrl, downloadUrl: inlineUrl, name, mime, kind };
  }

  try {
    const res = await fetch(inlineUrl);
    if (!res.ok) throw new Error('fetch failed');
    const blob = await res.blob();
    if (blob.size > BLOB_PREVIEW_MAX_BYTES) {
      return { url: inlineUrl, downloadUrl: inlineUrl, name, mime, kind };
    }
    const objectUrl = URL.createObjectURL(
      blob.type.includes('pdf') ? blob : new Blob([blob], { type: 'application/pdf' })
    );
    return { url: objectUrl, downloadUrl: inlineUrl, name, mime, kind };
  } catch {
    return { url: inlineUrl, downloadUrl: inlineUrl, name, mime, kind };
  }
}
